/**
 * Component: Karat Deduction Settings
 * Admin interface to configure rate deductions for each karat
 */

import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Form, InputNumber, Input, Space, message, Modal, Divider,
  Row, Col, Alert, Tooltip, Tag, Typography, Spin, Result, Switch,
} from 'antd';
import { EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, LockOutlined, ReloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  getAllDeductions,
  updateDeduction,
  deleteDeduction,
  initializeDefaults,
  getWebsiteMarkup,
  updateWebsiteMarkup,
} from '../api/karatDeductionApi';
import { fetchHallmarkGoldRate } from '../../../services/goldPriceService';

const { Title, Text } = Typography;

const KaratDeductionSettings = () => {
  const [deductions, setDeductions] = useState([]);
  const [liveRates, setLiveRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [showFormulaAlert, setShowFormulaAlert] = useState(() => {
    // Load from localStorage on initial mount
    const saved = localStorage.getItem('showKaratFormulaAlert');
    return saved === null || saved === 'true'; // Show by default, hide only if explicitly closed
  });
  const [karatSortOrder, setKaratSortOrder] = useState(() => {
    // Load from localStorage: 'desc' (24k top) or 'asc' (14k top)
    const saved = localStorage.getItem('karatDeductionsSortOrder');
    return saved || 'desc'; // Default: descending (24k at top)
  });
  const [editing, setEditing] = useState(null);
  const [websiteMarkup, setWebsiteMarkup] = useState(0);
  const [markupDraft, setMarkupDraft] = useState(0);
  const [markupSaving, setMarkupSaving] = useState(false);
  const [editForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();
  const { canEditField, canViewField } = usePermissions();
  const canManageKaratDeductions = canEditField('system', 'karatDeductions');
  const canViewKaratDeductions = canViewField('system', 'karatDeductions');

  // Dual deduction modal state
  const [deductionModalOpen, setDeductionModalOpen] = useState(false);
  const [deductionModalKarat, setDeductionModalKarat] = useState(null);
  const [deductionForm] = Form.useForm();
  const watchedWalkIn = Form.useWatch('walkInDeduction', deductionForm);
  const watchedWebsiteOverride = Form.useWatch('websiteOverride', deductionForm);

  // Website deduction mirrors walk-in unless overridden for that karat
  const websiteDeductionOf = (record) => (
    record.websiteOverride ? (record.websiteDeduction || 0) : (record.walkInDeduction || 0)
  );

  // Keep the website field visibly in sync with walk-in while not overridden
  useEffect(() => {
    if (deductionModalOpen && !watchedWebsiteOverride) {
      deductionForm.setFieldsValue({ websiteDeduction: watchedWalkIn ?? 0 });
    }
  }, [deductionModalOpen, watchedWalkIn, watchedWebsiteOverride, deductionForm]);

  useEffect(() => {
    loadDeductions();
    loadLiveRates(true); // true = silent mode for initial load
    loadWebsiteMarkup();
  }, []);

  const loadWebsiteMarkup = async () => {
    try {
      const pct = await getWebsiteMarkup();
      setWebsiteMarkup(pct);
      setMarkupDraft(pct);
    } catch (error) {
      console.error('❌ Failed to load website markup:', error);
    }
  };

  const handleSaveWebsiteMarkup = async () => {
    setMarkupSaving(true);
    try {
      const pct = await updateWebsiteMarkup(markupDraft ?? 0);
      setWebsiteMarkup(pct);
      setMarkupDraft(pct);
      messageApi.success(`Website markup set to ${pct}%`);
    } catch (error) {
      messageApi.error(error.response?.data?.error || error.message || 'Failed to update website markup');
    } finally {
      setMarkupSaving(false);
    }
  };

  const loadDeductions = async () => {
    setLoading(true);
    try {
      console.log('📥 Loading karat deductions...');
      const data = await getAllDeductions();
      console.log('✅ Loaded:', data);
      setDeductions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Failed to load karat deductions:', error);
      messageApi.error(error.response?.data?.message || error.message || 'Failed to load karat deductions');
    } finally {
      setLoading(false);
    }
  };

  const loadLiveRates = async (silent = false) => {
    setRatesLoading(true);
    try {
      console.log('📥 Loading live gold rates...');
      const rateData = await fetchHallmarkGoldRate();
      if (rateData?.rates) {
        console.log('✅ Loaded live rates:', rateData);
        setLiveRates(rateData.rates);
        // Only show notification on manual refresh, not on initial load
        if (!silent) {
          messageApi.success({
            content: `✅ Live rates refreshed successfully (${rateData.date || 'Today'})`,
            duration: 3,
          });
        }
      } else {
        throw new Error('No rate data received');
      }
    } catch (error) {
      console.error('❌ Failed to load live rates:', error);
      // Only show error notification on manual refresh
      if (!silent) {
        messageApi.error({
          content: `❌ Failed to refresh live rates: ${error.message || 'Unknown error'}`,
          duration: 4,
        });
      }
    } finally {
      setRatesLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditing(record.karat);
    editForm.setFieldsValue({
      description: record.description,
    });
  };

  // Handle opening dual deduction modal
  const handleOpenDeductionModal = (record) => {
    setDeductionModalKarat(record);
    // Reset form before setting new values
    deductionForm.resetFields();
    deductionForm.setFieldsValue({
      pledgeReleaseDeduction: record.pledgeReleaseDeduction || 0,
      walkInDeduction: record.walkInDeduction || 0,
      websiteOverride: !!record.websiteOverride,
      websiteDeduction: websiteDeductionOf(record),
    });
    setDeductionModalOpen(true);
  };

  // Handle saving dual deductions
  const handleSaveDualDeductions = async () => {
    try {
      const values = await deductionForm.validateFields();
      await updateDeduction(deductionModalKarat.karat, {
        pledgeReleaseDeduction: values.pledgeReleaseDeduction,
        walkInDeduction: values.walkInDeduction,
        websiteOverride: !!values.websiteOverride,
        websiteDeduction: values.websiteOverride ? values.websiteDeduction : values.walkInDeduction,
        description: deductionModalKarat.description,
      });
      messageApi.success(`Updated deductions for ${deductionModalKarat.karat}`);
      setDeductionModalOpen(false);
      deductionForm.resetFields();
      loadDeductions();
    } catch (error) {
      messageApi.error(error.message || 'Failed to update deductions');
    }
  };

  const handleDelete = (karat) => {
    modal.confirm({
      title: 'Delete Karat Deduction?',
      content: `Remove deduction configuration for ${karat}? It will be marked as inactive.`,
      okText: 'Yes, Delete',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteDeduction(karat);
          messageApi.success(`Deleted deduction for ${karat}`);
          loadDeductions();
        } catch (error) {
          messageApi.error('Failed to delete deduction');
        }
      },
    });
  };

  const handleInitDefaults = () => {
    console.log('🔘 Initialize Defaults button clicked');
    modal.confirm({
      title: 'Initialize Default Deductions?',
      content: 'This will create default entries for all 5 karats (24k, 23.8k, 22k, 18k, 14k) with zero deductions. Existing entries will not be overwritten.',
      okText: 'Initialize',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          console.log('🔄 Initializing defaults...');
          await initializeDefaults();
          console.log('✅ Defaults initialized successfully');
          messageApi.success('Default deductions initialized');
          loadDeductions();
        } catch (error) {
          console.error('❌ Failed to initialize defaults:', error);
          messageApi.error(error.response?.data?.message || error.message || 'Failed to initialize defaults');
        }
      },
    });
  };

  const handleToggleKaratSort = () => {
    const newOrder = karatSortOrder === 'desc' ? 'asc' : 'desc';
    setKaratSortOrder(newOrder);
    localStorage.setItem('karatDeductionsSortOrder', newOrder);
  };

  // Sort deductions based on karat
  const karatOrder = { '24k': 5, '23.8k': 4, '22k': 3, '18k': 2, '14k': 1 };
  const sortedDeductions = [...deductions].sort((a, b) => {
    const orderA = karatOrder[a.karat] || 0;
    const orderB = karatOrder[b.karat] || 0;
    return karatSortOrder === 'desc' ? orderB - orderA : orderA - orderB;
  });

  const columns = [
    {
      title: (
        <span
          onClick={handleToggleKaratSort}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          title="Click to sort"
        >
          Karat {karatSortOrder === 'desc' ? '↓' : '↑'}
        </span>
      ),
      dataIndex: 'karat',
      width: 100,
      render: (karat) => <Tag color="gold">{karat}</Tag>,
    },
    {
      title: 'Purity Code',
      dataIndex: 'purity',
      width: 120,
    },
    {
      title: 'Live Rate/gram',
      width: 150,
      render: (_, record) => {
        const rateObj = liveRates.find(r => r.purity === record.purity);
        return rateObj ? (
          <Tooltip title={`IBJA rate updated ${record.updated || 'today'}`}>
            <span style={{ fontSize: 14, fontWeight: 'bold', color: '#1890ff' }}>
              ₹ {rateObj.perGram?.toLocaleString('en-IN') || '-'}
            </span>
          </Tooltip>
        ) : (
          <span style={{ color: '#999', fontSize: 12 }}>-</span>
        );
      },
    },
    {
      title: 'Deduction/gram',
      dataIndex: 'deductionPerGram',
      width: 200,
      render: (value, record) => (
        <Button
          type="link"
          onClick={() => handleOpenDeductionModal(record)}
          disabled={!canManageKaratDeductions}
          style={{ padding: 0, height: 'auto' }}
        >
          <div style={{ cursor: 'pointer' }}>
            <div style={{ fontSize: 12, color: '#666' }}>Walk-In: <span style={{ fontSize: 14, fontWeight: 'bold', color: '#ff4d4f' }}>₹ {(record.walkInDeduction || 0).toFixed(2)}</span></div>
            <div style={{ fontSize: 12, color: '#666' }}>Pledge: <span style={{ fontSize: 14, fontWeight: 'bold', color: '#ff4d4f' }}>₹ {(record.pledgeReleaseDeduction || 0).toFixed(2)}</span></div>
            <div style={{ fontSize: 12, color: '#666' }}>
              Website: <span style={{ fontSize: 14, fontWeight: 'bold', color: '#ff4d4f' }}>₹ {websiteDeductionOf(record).toFixed(2)}</span>
              {' '}
              {record.websiteOverride
                ? <Tag color="orange" style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px' }}>Override</Tag>
                : <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px' }}>Synced</Tag>}
            </div>
          </div>
        </Button>
      ),
    },
    {
      title: (
        <Tooltip title="Live rate minus that channel's deduction. The website rate then adds the Website Rate Markup %.">
          <span>Display Rate/gram</span>
        </Tooltip>
      ),
      width: 300,
      render: (_, record) => {
        const rateObj = liveRates.find(r => r.purity === record.purity);
        if (!rateObj) return <span style={{ color: '#999', fontSize: 12 }}>-</span>;

        const live = rateObj.perGram || 0;
        const markup = websiteMarkup || 0;
        const fmt = (n) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const fmtShort = (n) => n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
        const formula = (deduction, withMarkup) => {
          let f = deduction ? `${fmtShort(live)} − ${fmtShort(deduction)}` : `${fmtShort(live)} (no deduction)`;
          if (withMarkup && markup) f = deduction ? `(${f}) + ${markup}%` : `${fmtShort(live)} + ${markup}%`;
          return f;
        };

        const websiteDeduction = websiteDeductionOf(record);
        const rows = [
          { label: 'Walk-In', value: Math.max(0, live - (record.walkInDeduction || 0)), formula: formula(record.walkInDeduction || 0, false) },
          { label: 'Pledge', value: Math.max(0, live - (record.pledgeReleaseDeduction || 0)), formula: formula(record.pledgeReleaseDeduction || 0, false) },
          { label: 'Website', value: Math.max(0, live - websiteDeduction) * (1 + markup / 100), formula: formula(websiteDeduction, true) },
        ];

        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr', columnGap: 8, rowGap: 2, alignItems: 'baseline' }}>
            {rows.map(r => (
              <React.Fragment key={r.label}>
                <span style={{ fontSize: 12, color: '#666' }}>{r.label}</span>
                <span style={{ fontSize: 13, fontWeight: 'bold', color: '#52c41a', textAlign: 'right', whiteSpace: 'nowrap' }}>₹ {fmt(r.value)}</span>
                <span style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>= {r.formula}</span>
              </React.Fragment>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (value) => <Text type="secondary">{value || '-'}</Text>,
    },
    {
      title: 'Actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title={!canManageKaratDeductions ? "You don't have permission to delete" : "Delete deduction"}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.karat)}
              size="small"
              disabled={!canManageKaratDeductions}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (!canViewKaratDeductions) {
    return (
      <>
        {contextHolder}
        {modalContextHolder}
        <div style={{ padding: 24 }}>
          <Result
            status="403"
            title="Access Denied"
            subTitle="You don't have permission to view Karat Rate Deductions settings."
            icon={<LockOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {contextHolder}
      {modalContextHolder}
      <div style={{ padding: 24 }}>
        <Card>
          <Row justify="space-between" align="middle" className="mb-4">
            <Col>
              <Title level={3} style={{ margin: 0 }}>
                Karat Rate Deductions
              </Title>
            </Col>
            <Col>
              <Space>
                <Tooltip
                  title="Fetch latest live gold rates from API"
                >
                  <Button
                    icon={ratesLoading ? <LoadingOutlined style={{ fontSize: 16, color: '#1890ff' }} /> : <ReloadOutlined />}
                    onClick={() => loadLiveRates(false)}
                    type={ratesLoading ? 'primary' : 'default'}
                    disabled={ratesLoading}
                  >
                    {ratesLoading ? 'Loading Rates...' : 'Refresh Live Rates'}
                  </Button>
                </Tooltip>
                <Tooltip
                  title={!canManageKaratDeductions ? "You don't have permission to manage karat deductions" : ""}
                >
                  <Button
                    type="primary"
                    onClick={handleInitDefaults}
                    disabled={!canManageKaratDeductions}
                  >
                    Initialize Defaults
                  </Button>
                </Tooltip>
              </Space>
            </Col>
          </Row>

          {showFormulaAlert && (
            <Alert
              type="info"
              message="Deduction Formula"
              description={
                <div>
                  When a user selects a karat in any form (Pledge Release, Walk-In, Gold Receipt), and on the website:
                  <br />
                  <strong style={{ color: '#1890ff' }}>
                    Display Rate = Live API Rate - Deduction Value
                  </strong>
                  <br />
                  For example: If 22k live rate is ₹13,768/gram and deduction is ₹50/gram,
                  the displayed rate will be ₹13,718/gram.
                  <br />
                  <span style={{ fontSize: 12, color: '#666', marginTop: 8, display: 'block' }}>
                    💡 Live rates are fetched from IBJA and shown for reference. Use the "Refresh Live Rates" button to get the latest values.
                  </span>
                </div>
              }
              showIcon
              closable
              onClose={() => {
                setShowFormulaAlert(false);
                localStorage.setItem('showKaratFormulaAlert', 'false');
              }}
              className="mb-3"
            />
          )}

          <Divider />

          <Card size="small" style={{ marginBottom: 16 }}>
            <Row justify="space-between" align="middle" gutter={[12, 12]}>
              <Col>
                <Text strong>Website Rate Markup GST%</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Website Rate = (Live Rate − Website Deduction) + {websiteMarkup}% — applied to today and all previous dates
                  </Text>
                </div>
              </Col>
              <Col>
                <Space>
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.5}
                    precision={2}
                    value={markupDraft}
                    onChange={(v) => setMarkupDraft(v ?? 0)}
                    addonAfter="%"
                    disabled={!canManageKaratDeductions}
                    style={{ width: 140 }}
                  />
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveWebsiteMarkup}
                    loading={markupSaving}
                    disabled={!canManageKaratDeductions || markupDraft === websiteMarkup}
                  >
                    Save
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          <Spin spinning={loading}>
            <Table
              dataSource={sortedDeductions}
              columns={columns}
              rowKey="karat"
              pagination={false}
              bordered
              size="small"
            />
          </Spin>

          <div style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              💡 Tip: Set deductions for markup, workshop fees, or profit margins. Leave 0 for no deduction.
            </Text>
          </div>
        </Card>
      </div>

      {/* Dual Deduction Modal */}
      <Modal
        title={`Deductions for ${deductionModalKarat?.karat}`}
        open={deductionModalOpen}
        onOk={handleSaveDualDeductions}
        onCancel={() => setDeductionModalOpen(false)}
        width={500}
      >
        <Form form={deductionForm} layout="vertical">
          <Form.Item
            label="Walk-In Case Deduction (₹/gram)"
            name="walkInDeduction"
            rules={[{ required: true, message: 'Please enter walk-in deduction' }]}
          >
            <InputNumber
              min={0}
              step={0.5}
              precision={2}
              placeholder="e.g., 25.50"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            label="Pledge Release Case Deduction (₹/gram)"
            name="pledgeReleaseDeduction"
            rules={[{ required: true, message: 'Please enter pledge release deduction' }]}
          >
            <InputNumber
              min={0}
              step={0.5}
              precision={2}
              placeholder="e.g., 30.00"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Divider style={{ margin: '8px 0 16px' }} />
          <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
            <Col><Text strong>Website Deduction (₹/gram)</Text></Col>
            <Col>
              <Space size={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>Override</Text>
                <Form.Item name="websiteOverride" valuePropName="checked" noStyle>
                  <Switch size="small" />
                </Form.Item>
              </Space>
            </Col>
          </Row>
          <Form.Item
            name="websiteDeduction"
            extra={watchedWebsiteOverride
              ? 'Override on: the website uses this value instead of the walk-in deduction.'
              : 'Synced with Walk-In. Turn on Override to set a different website deduction.'}
            rules={[{ required: !!watchedWebsiteOverride, message: 'Please enter website deduction' }]}
          >
            <InputNumber
              min={0}
              step={0.5}
              precision={2}
              disabled={!watchedWebsiteOverride}
              placeholder="e.g., 25.50"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Alert
            type="info"
            message="Display Rate Formula"
            description="Display Rate = Live Rate - Deduction, applied separately for Walk-In, Pledge and Website. The website rate then gets the Website Rate Markup % on top, and is published via the gold-rates API."
            showIcon
            style={{ marginTop: 16 }}
          />
        </Form>
      </Modal>
    </>
  );
};

export default KaratDeductionSettings;
